import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Shell from "@/components/Shell";
import { Button } from "@/components/ui/button";
import {
  DownloadSimple, Archive, Clock, HardDrives, Database, ArrowsClockwise, CloudArrowUp,
} from "@phosphor-icons/react";
import { SortableTh, useSortableRows } from "@/components/SortableTh";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtBytes(n) {
  if (!n && n !== 0) return "-";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function fmtDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("tr-TR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function Backups() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/backups`);
      setRows(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yedekler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createNow = async () => {
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/admin/backup`);
      toast.success(`Yedek oluşturuldu — ${data.total_documents} kayıt, ${fmtBytes(data.size_bytes)}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yedek oluşturulamadı");
    } finally {
      setCreating(false);
    }
  };

  const download = async (b) => {
    try {
      const resp = await axios.get(`${API}/admin/backups/${b.id}/download`,
                                    { responseType: "blob" });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = b.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("İndirme başarısız");
    }
  };

  const latest = rows[0];
  const latestAgeDays = latest
    ? Math.floor((Date.now() - new Date(latest.created_at).getTime()) / 86400000)
    : null;

  const { sort, toggleSort, sortedRows } = useSortableRows(rows, {
    defaultKey: "created_at", defaultDir: "desc",
    getters: {
      filename: (r) => r.filename || "",
      created_at: (r) => r.created_at || "",
      total_documents: (r) => Number(r.total_documents) || 0,
      size_bytes: (r) => Number(r.size_bytes) || 0,
      triggered_by: (r) => r.triggered_by || "",
    },
  });

  return (
    <Shell>
      <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-black text-[#1B4776] flex items-center gap-2">
              <HardDrives size={32} weight="duotone" className="text-[#95C11F]" />
              Veri Yedekleri
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Tüm koleksiyonları (sözleşme, RFQ, malzeme, tedarikçi, personel vb.) tek bir zip
              olarak arşivler. <b>Haftalık otomatik</b> yedek uygulama çalışırken alınır; ayrıca
              istediğinizde <b>manuel yedek</b> oluşturabilir ve indirebilirsiniz.
            </p>
          </div>
          <Button
            type="button"
            onClick={createNow}
            disabled={creating}
            data-testid="backup-create-now"
            className="h-10 rounded-none sm:rounded-sm bg-[#95C11F] hover:bg-[#7ea41a] text-white font-bold uppercase tracking-widest text-xs"
          >
            {creating
              ? (<><ArrowsClockwise size={16} className="mr-1.5 animate-spin" /> Alınıyor…</>)
              : (<><CloudArrowUp size={16} className="mr-1.5" /> Yeni Yedek Al</>)}
          </Button>
        </div>

        {/* Bilgi kartları */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={<Archive size={20} />} label="Toplam Yedek" value={rows.length}
                    hint="Son 8 yedek saklanır" />
          <StatCard icon={<Database size={20} />} label="En Son Kayıt Sayısı"
                    value={latest?.total_documents ?? "-"} />
          <StatCard icon={<HardDrives size={20} />} label="En Son Boyut"
                    value={fmtBytes(latest?.size_bytes)} />
          <StatCard icon={<Clock size={20} />} label="Son Yedek Yaşı"
                    value={latestAgeDays === null ? "Henüz yok"
                          : latestAgeDays === 0 ? "Bugün" : `${latestAgeDays} gün önce`}
                    warn={latestAgeDays !== null && latestAgeDays > 8} />
        </div>

        {/* Tablo */}
        <div className="bg-white border border-slate-200 rounded-none sm:rounded-sm overflow-hidden">
          <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-700">
              Yedek Listesi
            </div>
            <button
              type="button"
              onClick={load}
              className="text-[10px] uppercase tracking-widest font-bold text-slate-500 hover:text-slate-800 flex items-center gap-1"
              data-testid="backup-refresh"
            >
              <ArrowsClockwise size={12} /> Yenile
            </button>
          </div>

          {loading && (
            <div className="p-10 text-center text-slate-500 text-sm">Yükleniyor…</div>
          )}

          {!loading && rows.length === 0 && (
            <div className="p-10 text-center text-slate-500">
              <Archive size={48} weight="duotone" className="mx-auto text-slate-300 mb-2" />
              <div className="text-sm">Henüz yedek yok.</div>
              <div className="text-xs mt-1">Yukarıdan "Yeni Yedek Al" butonu ile ilk yedeği alın.</div>
            </div>
          )}

          {!loading && rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-600 font-bold">
                  <tr>
                    <SortableTh label="Dosya" k="filename" sort={sort} onSort={toggleSort} className="px-4 py-2 text-left" />
                    <SortableTh label="Tarih" k="created_at" sort={sort} onSort={toggleSort} className="px-4 py-2 text-left" />
                    <SortableTh label="Kayıt" k="total_documents" sort={sort} onSort={toggleSort} className="px-4 py-2 text-right" align="right" />
                    <SortableTh label="Boyut" k="size_bytes" sort={sort} onSort={toggleSort} className="px-4 py-2 text-right" align="right" />
                    <SortableTh label="Kaynak" k="triggered_by" sort={sort} onSort={toggleSort} className="px-4 py-2 text-left" />
                    <th className="px-4 py-2 text-right">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((b, i) => (
                    <tr key={b.id} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/40"}>
                      <td className="px-4 py-2 font-mono text-xs text-[#1B4776]">{b.filename}</td>
                      <td className="px-4 py-2 text-xs text-slate-700">{fmtDate(b.created_at)}</td>
                      <td className="px-4 py-2 text-right font-mono text-xs">{b.total_documents}</td>
                      <td className="px-4 py-2 text-right font-mono text-xs">{fmtBytes(b.size_bytes)}</td>
                      <td className="px-4 py-2 text-xs">
                        {(b.triggered_by || "").startsWith("manual") ? (
                          <span className="inline-block px-1.5 py-0.5 bg-blue-50 text-blue-800 border border-blue-200 rounded-none sm:rounded-sm text-[10px] font-bold">MANUEL</span>
                        ) : (b.triggered_by || "").startsWith("scheduled") ? (
                          <span className="inline-block px-1.5 py-0.5 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-none sm:rounded-sm text-[10px] font-bold">HAFTALIK</span>
                        ) : (
                          <span className="text-slate-500 text-[10px]">{b.triggered_by || "-"}</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => download(b)}
                          data-testid={`backup-download-${b.id}`}
                          className="inline-flex items-center gap-1 px-2 py-1 border border-[#1B4776] text-[#1B4776] hover:bg-[#1B4776] hover:text-white text-[10px] uppercase tracking-widest font-bold rounded-none sm:rounded-sm transition-colors"
                        >
                          <DownloadSimple size={12} /> İndir
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="text-xs text-slate-500 border-l-2 border-slate-300 pl-3 space-y-1">
          <p>• <b>Otomatik zamanlama</b>: Uygulama başlangıcında ve her 24 saatte bir kontrol edilir. Son 7 gün içinde yedek yoksa yeni bir tanesi alınır.</p>
          <p>• <b>Kapsam</b>: users, sites, suppliers, contracts, rfqs, materials, purchase_requests, employees, timesheet, roles, user_invites, files, material_categories.</p>
          <p>• <b>Saklama</b>: Son <b>8</b> yedek listede kalır; daha eskiler metadata'dan silinir (storage'daki fiziksel dosyalar orada kalabilir).</p>
          <p>• <b>Geri Yükleme</b>: Bir yedeği indirdikten sonra zip'i açıp içindeki JSON'ları <code>/api/admin/restore</code> endpoint'ine yükleyerek geri yükleyebilirsiniz.</p>
        </div>
      </div>
    </Shell>
  );
}

function StatCard({ icon, label, value, hint, warn }) {
  return (
    <div className={`p-3 border rounded-none sm:rounded-sm ${warn ? "bg-amber-50 border-amber-300" : "bg-white border-slate-200"}`}>
      <div className={`text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5 ${warn ? "text-amber-800" : "text-slate-600"}`}>
        {icon} {label}
      </div>
      <div className={`text-2xl font-black mt-1 ${warn ? "text-amber-800" : "text-[#1B4776]"}`}>{value}</div>
      {hint && <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}
